import type {
  RedencionBonoCatalogSnapshot,
  RedencionBonoHeader,
  RedencionBonoParticipanteFormPayload,
} from '../../domain/redencionBonoModel.ts';
import {
  comunaOCorregimientoPdf,
  responsableLabelForPdf,
  textoMayusEs,
  tipoBonoCeldaPdf,
} from '../../../shared/report-pdf/infrastructure/foGesrFieldFormattingCore.ts';
import type {
  RedencionBonoReportPdfInput,
  RedencionBonoReportPdfRow,
} from '../../../shared/report-pdf/redencion-bono-fo-1775/redencionBonoReportPdfData.ts';
import {
  comunaEtiquetaDesdeBarrio,
  type BarrioComunaLookup,
} from '../../../shared/locations/domain/barrioComunaLookup.ts';

export type { RedencionBonoReportPdfInput, RedencionBonoReportPdfRow } from '../../../shared/report-pdf/redencion-bono-fo-1775/redencionBonoReportPdfData.ts';

export type ParticipanteMini = {
  documento: string;
  nombre: string;
  barrioId: number;
} | null;
/** Provider line for PDF header/footer. */


function proveedorLinePdf(prov: { id: number; nombre: string; direccion: string }): string {
  const nombre = textoMayusEs(prov.nombre || '—');
  const dir = textoMayusEs((prov.direccion || '').trim() || '—');
  return `${prov.id}: ${nombre} - ${dir}`;
}
/** Delivery number as printed on the PDF (last numeric run or full text). */


function numeroEntregaParaPdf(entregaIdStr: string): string {
  const nums = entregaIdStr.match(/\d+/g);
  if (nums?.length) return nums.at(-1) ?? '—';
  return entregaIdStr.trim() || '—';
}
/** Authorized-person label for PDF rows. */


function nombreAutorizadaRb(p: RedencionBonoParticipanteFormPayload): string {
  if (!p.personaAutorizada) return 'NO';
  const n = p.nombrePersonaAutorizada?.trim();
  return n ? textoMayusEs(n) : '—';
}
/** Remainder flag rendered as YES/NO REMANENTE on PDF. */


function remanentePdf(esRemanente: boolean): string {
  return esRemanente ? 'REMANENTE' : 'NO REMANENTE';
}
/** Location block from provider address + captured device coordinates. */
function ubicacionDesdeCaptura(
  prov: { direccion: string },
  header: { latitud: string; longitud: string },
): string {
  const parts: string[] = [];
  const dir = (prov.direccion ?? '').trim();
  const lat = (header.latitud ?? '').trim();
  const lon = (header.longitud ?? '').trim();
  if (dir) parts.push(textoMayusEs(dir));
  if (lat || lon) parts.push([lat, lon].filter(Boolean).join(', '));
  return parts.length ? parts.join('\n') : '';
}
/** Collect redencion bono report pdf input. */


export function collectRedencionBonoReportPdfInput(opts: {
  publicId: string;
  header: RedencionBonoHeader;
  participantes: RedencionBonoParticipanteFormPayload[];
  catalog: RedencionBonoCatalogSnapshot;
  barrioComunaLookup: BarrioComunaLookup;
  findParticipante: (participanteId: number) => ParticipanteMini;
}): { ok: true; input: RedencionBonoReportPdfInput } | { ok: false; message: string } {
  const { publicId, header, participantes, catalog, barrioComunaLookup, findParticipante } = opts;
  if (header.proveedorId == null || header.proveedorId <= 0) {
    return { ok: false, message: 'Seleccione un proveedor para generar el PDF.' };
  }
  if (participantes.length === 0) {
    return { ok: false, message: 'Agregue al menos un participante.' };
  }
  if (!header.fecha.trim()) {
    return { ok: false, message: 'Indique la fecha y hora.' };
  }
  if (!header.responsableId) {
    return { ok: false, message: 'Indique el responsable.' };
  }
  if (header.esRemanente == null) {
    return { ok: false, message: 'Indique si es remanente.' };
  }

  const prov = catalog.proveedores.find((p) => p.id === header.proveedorId);
  if (!prov) return { ok: false, message: 'Proveedor no válido.' };
  const grupos = catalog.gruposAlimentos.filter((g) => prov.gruposAlimentosIds.includes(g.id));
  const grupoTxt = grupos.length > 0
    ? textoMayusEs(grupos.map(g => g.nombre.replaceAll(/\s+/g, ' ')).join(', '))
    : '—';

  const rows: RedencionBonoReportPdfRow[] = participantes.map((line) => {
    const pr = findParticipante(line.participanteId);
    const barrioId = pr?.barrioId ?? 0;
    const comunaLbl = comunaEtiquetaDesdeBarrio(barrioComunaLookup, barrioId);
    const comunaEt = comunaOCorregimientoPdf(comunaLbl);
    const captured = line.tipoBono?.trim() ?? '';
    const tipoBono =
      captured.length > 0 ? textoMayusEs(captured.replaceAll(/\s+/g, ' ')) : tipoBonoCeldaPdf(comunaEt);
    const doc = (pr?.documento ?? '').trim() || '—';
    const nom = (line.nombreParticipante || pr?.nombre || '').trim() || '—';
    return {
      bono: line.bono?.trim() ? line.bono.trim() : line.bonoId ? String(line.bonoId) : '—',
      tipoBono,
      documentoParticipante: doc,
      nombreParticipante: textoMayusEs(nom),
      nombrePersonaAutorizada: nombreAutorizadaRb(line),
      autorizaPrivacidad: line.politicaTratamientos ? 'SI' : 'NO',
      registroFotografico: line.registroFotografico ?? '',
      firma: line.firmaBase64 ?? '',
    };
  });

  return {
    ok: true,
    input: {
      docPublicId: publicId,
      proveedorLine: proveedorLinePdf(prov),
      responsable: responsableLabelForPdf(header.responsableId, header.responsableNombre),
      fechaRegistroRaw: header.fecha.trim(),
      grupoAlimentos: grupoTxt,
      entregaN: numeroEntregaParaPdf(header.entregaId ? String(header.entregaId) : ''),
      remanente: remanentePdf(header.esRemanente === true),
      latitud: header.latitud ?? '',
      longitud: header.longitud ?? '',
      ubicacionTexto: ubicacionDesdeCaptura(prov, header),
      rows,
    },
  };
}
